import { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Pencil, Check, X, Star, TrendingUp, DollarSign, BarChart3, Info, CheckCircle, AlertCircle, Trash2, ArrowRightLeft } from 'lucide-react';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useProducts, useUpdateProduct, useDeleteProduct, type Product } from '@/hooks/useProducts';
import { AlternativeToSelect } from '@/components/AlternativeToSelect';
import { useAdmin } from '@/hooks/useAdmin';
import { getDisplayTrustROI, getVerifiedByLabel } from '@/lib/roi-calculator';
import { toast } from '@/hooks/use-toast';

type SortOption = 'trustroi' | 'price';

const RANK_MEDALS = ['🥇', '🥈', '🥉'];

const CATEGORY_COLORS: Record<string, string> = {
  AI: 'bg-purple-500/15 text-purple-400 border-purple-500/25',
  Marketing: 'bg-blue-500/15 text-blue-400 border-blue-500/25',
  Productivity: 'bg-green-500/15 text-green-400 border-green-500/25',
  Analytics: 'bg-orange-500/15 text-orange-400 border-orange-500/25',
  CRM: 'bg-pink-500/15 text-pink-400 border-pink-500/25',
  Design: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/25',
  Development: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/25',
  Finance: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
};

function getCategoryStyle(category: string) {
  return CATEGORY_COLORS[category] || 'bg-secondary text-muted-foreground border-border';
}

function LeaderboardSkeleton() {
  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="flex items-center justify-center gap-6 py-3 px-4 border-b border-border bg-secondary/30">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-4 w-28" />
        ))}
      </div>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent border-border">
            <TableHead className="w-10 text-center">#</TableHead>
            <TableHead>Product</TableHead>
            <TableHead className="text-right">Price</TableHead>
            <TableHead className="text-right">Return on Investment</TableHead>
             <TableHead className="text-right">Verified</TableHead>
             <TableHead className="text-center max-w-[150px]">Verdict</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 8 }).map((_, i) => (
            <TableRow key={i} className="border-border">
              <TableCell className="text-center"><Skeleton className="h-4 w-6 mx-auto" /></TableCell>
              <TableCell>
                <div className="flex items-center gap-3">
                  <Skeleton className="w-7 h-7 rounded" />
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-3 w-36" />
                  </div>
                </div>
              </TableCell>
              <TableCell className="text-right"><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
              <TableCell className="text-right"><Skeleton className="h-4 w-10 ml-auto" /></TableCell>
              <TableCell className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
              <TableCell className="text-center"><Skeleton className="h-4 w-20 mx-auto" /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function Leaderboard({ searchQuery = '' }: { searchQuery?: string }) {
  const [searchParams] = useSearchParams();
  const urlSort = searchParams.get('sort');
  const [sortBy, setSortBy] = useState<SortOption>('trustroi');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{ name: string; description: string; logo_url: string; website_url: string; marketing_gap: string; alternative_to: string[] }>({ name: '', description: '', logo_url: '', website_url: '', marketing_gap: '', alternative_to: [] });

  const { data: products, isLoading } = useProducts();
  const { isAdmin } = useAdmin();
  const updateProduct = useUpdateProduct();
  const deleteProduct = useDeleteProduct();

  const categories = useMemo(() => {
    if (!products) return [];
    return [...new Set(products.map((p) => p.category))].sort();
  }, [products]);

  const entries = useMemo(() => {
    if (!products) return [];
    return products.map((p) => {
      const trustRoi = getDisplayTrustROI(p as any);
      const verifiedBy = getVerifiedByLabel(p.verification_status);
      return { ...p, trustRoi, verifiedBy };
    });
  }, [products]);

  const filtered = useMemo(() => {
    let list = [...entries];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((e) => e.name.toLowerCase().includes(q) || e.description.toLowerCase().includes(q));
    }
    if (categoryFilter !== 'all') {
      list = list.filter((e) => e.category === categoryFilter);
    }
    if (urlSort === 'new') {
      list.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    } else {
      list.sort((a, b) => {
        if (sortBy === 'trustroi') return (b.trustRoi ?? -1) - (a.trustRoi ?? -1);
        return Number(a.pricing_monthly) - Number(b.pricing_monthly);
      });
    }
    return list;
  }, [entries, categoryFilter, sortBy, urlSort, searchQuery]);

  // Aggregate stats
  const stats = useMemo(() => {
    if (!entries.length) return null;
    const withRoi = entries.filter(e => e.trustRoi !== null);
    const avgRoi = withRoi.length > 0
      ? Math.round((withRoi.reduce((s, e) => s + (e.trustRoi || 0), 0) / withRoi.length) * 10) / 10
      : 0;
    return {
      total: entries.length,
      avgRoi,
    };
  }, [entries]);

  if (isLoading || !products) {
    return (
      <section id="leaderboard">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-xl font-bold text-foreground">🏆 TrustROI Rankings</h2>
        </div>
        <LeaderboardSkeleton />
      </section>
    );
  }

  const startEdit = (p: Product) => {
    setEditingId(p.id);
    setEditValues({ name: p.name, description: p.description, logo_url: p.logo_url, website_url: p.website_url, marketing_gap: (p as any).marketing_gap || '', alternative_to: (p as any).alternative_to || [] });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    await updateProduct.mutateAsync({ id: editingId, updates: editValues as any });
    setEditingId(null);
  };

  const handleDelete = async (id: string, name: string) => {
    try {
      await deleteProduct.mutateAsync(id);
      toast({ title: `Deleted "${name}"` });
    } catch (err: any) {
      toast({ title: 'Delete failed', description: err.message, variant: 'destructive' });
    }
  };

  return (
    <section id="leaderboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
        <h2 className="font-display text-lg sm:text-xl font-bold text-foreground">🏆 TrustROI Rankings</h2>
        <div className="flex gap-2">
          <Select value={categoryFilter} onValueChange={(v) => setCategoryFilter(v)}>
            <SelectTrigger className="w-[120px] sm:w-[140px] h-8 text-xs bg-secondary"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortOption)}>
            <SelectTrigger className="w-[130px] sm:w-[150px] h-8 text-xs bg-secondary"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="trustroi">Sort by TrustROI</SelectItem>
              <SelectItem value="price">Sort by Price</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        {/* Stats bar */}
        {stats && (
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 md:gap-8 py-2.5 px-3 sm:px-4 border-b border-border bg-secondary/30 text-[11px] sm:text-xs text-muted-foreground">
            <span className="flex items-center gap-1 sm:gap-1.5">
              <BarChart3 className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-primary" />
              <strong className="text-foreground">{stats.total}</strong> tools
            </span>
            <span className="flex items-center gap-1 sm:gap-1.5">
              <TrendingUp className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-primary" />
              Avg <strong className="text-foreground">{stats.avgRoi}x</strong> TrustROI
            </span>
          </div>
        )}

        <TooltipProvider delayDuration={100}>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead className="w-10 text-center">#</TableHead>
                <TableHead>Product</TableHead>
                <TableHead className="text-right">Price</TableHead>
                <TableHead className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    Return on Investment
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button type="button" aria-label="What is TrustROI score?" className="inline-flex">
                          <Info className="h-3 w-3 text-muted-foreground/50 hover:text-muted-foreground cursor-help" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[250px] text-xs">
                        TrustROI = (Verified Users × Avg Retention) / Price. Higher means more customer value per dollar.
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </TableHead>
                <TableHead className="text-right">Verified</TableHead>
                <TableHead className="text-center max-w-[150px]">Verdict</TableHead>
                <TableHead className="text-center max-w-[180px]">Alternative To</TableHead>
                {isAdmin && <TableHead className="w-10"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((entry, i) => {
                const isPaid = entry.listing_tier === 'paid';
                const medal = RANK_MEDALS[i];

                return (
                  <TableRow key={entry.id} className="border-border hover:bg-secondary/50">
                    <TableCell className="text-center font-mono text-sm">
                      {medal ? <span className="text-base">{medal}</span> : <span className="text-muted-foreground">{i + 1}</span>}
                    </TableCell>
                    <TableCell>
                      {editingId === entry.id ? (
                        <div className="flex flex-col gap-1.5">
                          <Input value={editValues.name} onChange={(e) => setEditValues(v => ({ ...v, name: e.target.value }))} className="h-7 text-sm" />
                          <Input value={editValues.description} onChange={(e) => setEditValues(v => ({ ...v, description: e.target.value }))} className="h-7 text-xs" />
                          <Input value={editValues.logo_url} onChange={(e) => setEditValues(v => ({ ...v, logo_url: e.target.value }))} className="h-7 text-xs" placeholder="Logo URL" />
                          <Input value={editValues.website_url} onChange={(e) => setEditValues(v => ({ ...v, website_url: e.target.value }))} className="h-7 text-xs" placeholder="Website URL (with UTM tags)" />
                          <Input value={editValues.marketing_gap} onChange={(e) => setEditValues(v => ({ ...v, marketing_gap: e.target.value }))} className="h-7 text-xs" placeholder="Verdict 🟢🔴" />
                        </div>
                      ) : (
                        <Link to={`/product/${entry.slug}`} className="flex items-center gap-3 group">
                          <img src={entry.logo_url} alt={`${entry.name} logo — ${entry.category} tool`} loading="lazy" className="w-7 h-7 rounded object-contain" />
                          <div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors">{entry.name}</span>
                              {isPaid && (
                                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px] px-1.5 py-0 gap-0.5">
                                  <Star className="h-2.5 w-2.5 fill-current" />
                                  Featured
                                </Badge>
                              )}
                              <Badge className={`text-[10px] px-1.5 py-0 border ${getCategoryStyle(entry.category)}`}>
                                {entry.category}
                              </Badge>
                            </div>
                            <div className="text-xs text-muted-foreground line-clamp-1 max-w-[140px] sm:max-w-[200px]">{entry.description}</div>
                          </div>
                        </Link>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-mono text-xs sm:text-sm text-foreground">${Number(entry.verified_pricing || entry.pricing_monthly)}/mo</span>
                    </TableCell>
                    <TableCell className="text-right">
                      {entry.trustRoi !== null ? (
                        <span className="font-mono font-bold text-primary">{entry.trustRoi}x</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">N/A</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {entry.verifiedBy ? (
                        <Badge className="text-[10px] px-1.5 py-0 border bg-green-500/15 text-green-400 border-green-500/25">
                          <CheckCircle className="h-2.5 w-2.5 mr-0.5" />
                          Yes
                        </Badge>
                      ) : (
                        <Badge className="bg-muted text-muted-foreground border-border text-[10px] px-1.5 py-0 gap-0.5">
                          <AlertCircle className="h-2.5 w-2.5" /> No
                        </Badge>
                      )}
                    </TableCell>
                     <TableCell className="text-center text-xs max-w-[150px]">
                      {(entry as any).marketing_gap ? (
                        <span className="text-foreground whitespace-normal line-clamp-2">{(entry as any).marketing_gap}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center max-w-[180px]">
                      {editingId === entry.id ? (
                        <AlternativeToSelect
                          value={editValues.alternative_to}
                          onChange={(slugs) => setEditValues(v => ({ ...v, alternative_to: slugs }))}
                          excludeSlug={entry.slug}
                        />
                      ) : (
                        <div className="flex flex-wrap gap-1 justify-center">
                          {((entry as any).alternative_to || []).length > 0 ? (
                            ((entry as any).alternative_to as string[]).map((altSlug) => {
                              const altProduct = products?.find(p => p.slug === altSlug);
                              if (!altProduct) return null;
                              return (
                                <Link key={altSlug} to={`/product/${altSlug}`}>
                                  <Badge className="bg-secondary text-foreground border-border gap-1 pl-1 pr-1.5 py-0.5 text-[10px] hover:bg-secondary/80">
                                    <img src={altProduct.logo_url} alt="" className="w-3.5 h-3.5 rounded object-contain" />
                                    {altProduct.name}
                                  </Badge>
                                </Link>
                              );
                            })
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </div>
                      )}
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        <div className="flex gap-1">
                          {editingId === entry.id ? (
                            <>
                              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={saveEdit} aria-label="Save changes"><Check className="h-3 w-3" /></Button>
                              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setEditingId(null)} aria-label="Cancel editing"><X className="h-3 w-3" /></Button>
                            </>
                          ) : (
                            <>
                              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => startEdit(entry)} aria-label={`Edit ${entry.name}`}>
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive hover:text-destructive" aria-label={`Delete ${entry.name}`}>
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete "{entry.name}"?</AlertDialogTitle>
                                    <AlertDialogDescription>This will permanently remove this listing. This action cannot be undone.</AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDelete(entry.id, entry.name)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No tools in this category yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TooltipProvider>
      </div>
    </section>
  );
}
